import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { getEjercicio } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ArrowLeft, PieChart, FileText, BarChart3, TrendingUp, ArrowRightLeft, BookOpen, Landmark, Percent } from "lucide-react";

// Importar componentes de contenido
import DashboardContent from "@/components/analisis/DashboardContent";
import BalanceContent from "@/components/analisis/BalanceContent";
import PygContent from "@/components/analisis/PygContent";
import EbitdaContent from "@/components/analisis/EbitdaContent";
import FlujoCajaContent from "@/components/analisis/FlujoCajaContent";
import MargenBrutoContent from "@/components/analisis/MargenBrutoContent";

export default function EjercicioAnalisisPage() {
  const { ejercicioId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ejercicio, setEjercicio] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Obtener la pestaña activa de la URL o usar "dashboard" por defecto
  const activeTab = searchParams.get("tab") || "dashboard";

  useEffect(() => {
    loadEjercicio();
  }, [ejercicioId]);

  const loadEjercicio = async () => {
    try {
      const response = await getEjercicio(ejercicioId);
      setEjercicio(response.data);
    } catch (error) {
      toast.error("Error al cargar ejercicio");
      navigate("/empresas");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (value) => {
    setSearchParams({ tab: value });
  };

  const goBack = () => {
    if (ejercicio?.empresa_id) {
      navigate(`/empresas/${ejercicio.empresa_id}`);
    } else {
      navigate("/empresas");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header con título y botón de volver */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={goBack} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="page-title">
              Análisis Ejercicio {ejercicio?.anio}
            </h1>
            <p className="text-sm text-muted-foreground">
              Informes financieros y ratios de rendimiento
            </p>
          </div>
        </div>
      </div>

      {/* Pestañas horizontales. W19.3b ("ponerlo el botón al lado de los
          otros"): el acceso a Asientos manuales va en la misma fila que
          Dashboard/Balance/PyG/EBITDA/Flujos, no solo en el sidebar. */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div className="flex flex-wrap items-center gap-2">
          <TabsList className="grid w-full grid-cols-6 sm:w-auto sm:inline-flex" data-testid="analysis-tabs">
            <TabsTrigger value="dashboard" className="gap-2" data-testid="tab-dashboard">
              <PieChart className="h-4 w-4 hidden sm:block" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="balance" className="gap-2" data-testid="tab-balance">
              <FileText className="h-4 w-4 hidden sm:block" />
              Balance
            </TabsTrigger>
            <TabsTrigger value="pyg" className="gap-2" data-testid="tab-pyg">
              <BarChart3 className="h-4 w-4 hidden sm:block" />
              PyG
            </TabsTrigger>
            <TabsTrigger value="ebitda" className="gap-2" data-testid="tab-ebitda">
              <TrendingUp className="h-4 w-4 hidden sm:block" />
              EBITDA
            </TabsTrigger>
            <TabsTrigger value="flujos" className="gap-2" data-testid="tab-flujos">
              <ArrowRightLeft className="h-4 w-4 hidden sm:block" />
              Flujos
            </TabsTrigger>
            {/* Margen Bruto configurable (petición Héctor sept-2026) */}
            <TabsTrigger value="margen" className="gap-2" data-testid="tab-margen">
              <Percent className="h-4 w-4 hidden sm:block" />
              Margen Bruto
            </TabsTrigger>
          </TabsList>
          <Button
            variant="outline"
            size="sm"
            className="gap-2 h-9"
            onClick={() => navigate(`/ejercicios/${ejercicioId}/asientos`)}
            data-testid="tab-asientos"
          >
            <BookOpen className="h-4 w-4 hidden sm:block" />
            Asientos
          </Button>
          {/* Libro contable navegable (S&S → mayor → asiento): solo existe en
              ejercicios con diario de Holded (API o Excel) en holded_apuntes. */}
          {ejercicio?.origen_datos?.startsWith("HOLDED") && (
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => navigate(`/ejercicios/${ejercicioId}/contabilidad`)}
              data-testid="tab-contabilidad"
            >
              <Landmark className="h-4 w-4 hidden sm:block" />
              Contabilidad
            </Button>
          )}
        </div>

        <TabsContent value="dashboard" className="mt-4">
          <DashboardContent ejercicioId={ejercicioId} />
        </TabsContent>

        <TabsContent value="balance" className="mt-4">
          <BalanceContent ejercicioId={ejercicioId} />
        </TabsContent>

        <TabsContent value="pyg" className="mt-4">
          <PygContent ejercicioId={ejercicioId} />
        </TabsContent>

        <TabsContent value="ebitda" className="mt-4">
          <EbitdaContent ejercicioId={ejercicioId} ejercicio={ejercicio} />
        </TabsContent>

        <TabsContent value="flujos" className="mt-4">
          <FlujoCajaContent ejercicioId={ejercicioId} />
        </TabsContent>

        <TabsContent value="margen" className="mt-4">
          <MargenBrutoContent ejercicioId={ejercicioId} empresaId={ejercicio?.empresa_id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
